import type { ReactElement } from 'react';
import type { ResolvedHeroPlugin } from './contract';

/**
 * The neutral catalog/wardrobe TILE — a calm tinted dot for a hero with no authored item art. Exported so
 * `heroPluginFor` can use it as the base `renderItemIcon` default for a bespoke plugin that omits one.
 */
export function neutralItemIcon(slug: string, className?: string): ReactElement {
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <circle cx={50} cy={50} r={30} fill="var(--color-secondary)" />
      <title>{slug}</title>
    </svg>
  );
}

/**
 * A calm, TOKEN-TINTED placeholder figure (ADR-0110) — a soft head-and-shoulders silhouette in the family
 * palette. It adapts to the theme (+ dark mode) automatically and is reduced-motion-safe. Drawn for any
 * archetype with no bespoke plugin (a data-only hero, or a not-yet-chosen presence), so the render never
 * breaks; it replaces the retired procedural orb + symbol sheet.
 */
function neutralFigure(className?: string): ReactElement {
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <circle cx={50} cy={48} r={42} fill="var(--color-accent)" opacity={0.16} />
      <circle cx={50} cy={40} r={15} fill="var(--color-primary)" />
      <path d="M30 86 V72 a20 20 0 0 1 40 0 V86 Z" fill="var(--color-primary)" />
    </svg>
  );
}

/**
 * The NEUTRAL plugin (ADR-0099 / ADR-0100 / ADR-0110) for any archetype WITHOUT a bespoke {@link HeroPlugin}
 * — a brand-new data-only hero, or a not-yet-chosen presence. It draws the calm {@link neutralFigure}
 * placeholder with NO cosmetics: a hero's looks are authored in its own coordinate space, so there is nothing
 * to compose until it ships a plugin. The render never breaks on an unknown hero — this is the
 * graceful-degradation guarantee. It is a fully RESOLVED plugin (every method present).
 */
export function neutralPlugin(archetype: string): ResolvedHeroPlugin {
  return {
    archetype,
    renderFigure: ({ className }) => neutralFigure(className),
    renderAvatar: () => neutralFigure(),
    renderScene: () => null,
    renderItemIcon: ({ slug, className }) => neutralItemIcon(slug, className),
  };
}
